import DiagnosticsStore from "@/features/diagnostics/store/diagnostics-store.ts";
import type { SmartNumberMapping } from "@/features/sub-families/types";
import type { Nullable } from "@/types";
import { HISTORY_INDEX_KEY } from "../resources/constants.ts";
import type { HistoryEntry, HistoryState } from "../types";

const PATCHED = Symbol.for("deltalight.historyPatched");

type PatchedGlobal = typeof globalThis & { [PATCHED]?: boolean };

export default class HistoryPatch {
    #currentSmartNumber: Nullable<string> = null;
    #currentIndex = -1;
    #restoring = false;

    readonly #stack: HistoryEntry[] = [];

    constructor() {
        this.#patch();
    }

    get currentSmartNumber(): Nullable<string> {
        return this.#currentSmartNumber;
    }

    set currentSmartNumber(value: Nullable<string>) {
        this.#currentSmartNumber = value;
    }

    set restoring(value: boolean) {
        this.#restoring = value;
    }

    push(smartNumber: string, mapping: SmartNumberMapping) {
        // Discard forward entries when pushing from a Back position (mirrors browser behavior).
        // e.g. stack = ["A", "B", "C"], pointer at 1 ("B") -> truncate to ["A", "B"] -> push "D" -> ["A", "B", "D"]
        const removedIds = this.#stack.slice(this.#currentIndex + 1).map((entry) => entry.id);
        this.#stack.length = this.#currentIndex + 1;

        if (removedIds.length > 0) {
            DiagnosticsStore.removeHistoryStates(removedIds);
        }

        const entry = this.#createEntry(smartNumber, mapping);
        this.#stack.push(entry);
        this.#currentIndex = this.#stack.length - 1;

        DiagnosticsStore.collectHistoryState(entry.id, smartNumber, mapping);
    }

    replace(smartNumber: string, mapping: SmartNumberMapping) {
        const entry = this.#createEntry(smartNumber, mapping);
        const existingId = this.#stack[this.#currentIndex]?.id;

        if (this.#currentIndex < 0) {
            this.#stack.push(entry);
            this.#currentIndex = 0;
        } else {
            this.#stack[this.#currentIndex] = entry;
        }

        if (existingId) {
            DiagnosticsStore.removeHistoryStates([existingId]);
        }

        DiagnosticsStore.collectHistoryState(entry.id, smartNumber, mapping);
    }

    resolve(state: unknown): Nullable<HistoryEntry> {
        if (typeof state === "object" && state !== null) {
            const historyState = state as HistoryState;
            const index = historyState[HISTORY_INDEX_KEY];

            if (typeof index === "number" && this.#stack[index]) {
                const removedIds = this.#stack.slice(index + 1).map((entry) => entry.id);

                if (removedIds.length > 0) {
                    DiagnosticsStore.removeHistoryStates(removedIds);
                }

                this.#currentIndex = index;
                return this.#stack[index];
            }
        }

        return null;
    }

    #patch() {
        const global = globalThis as PatchedGlobal;
        if (global[PATCHED]) return;

        const originalPushState = history.pushState.bind(history);
        const originalReplaceState = history.replaceState.bind(history);

        history.pushState = (state: unknown, title: string, url?: Nullable<string | URL>) => {
            if (this.#restoring) return undefined;

            return originalPushState(this.#enrichState(state), title, url);
        };

        history.replaceState = (state: unknown, title: string, url?: Nullable<string | URL>) => {
            return originalReplaceState(this.#enrichState(state), title, url);
        };

        global[PATCHED] = true;
    }

    #enrichState(state: unknown): HistoryState {
        const base = (typeof state === "object" && state !== null ? state : {}) as Record<string, unknown>;

        return {
            ...base,
            [HISTORY_INDEX_KEY]: this.#currentIndex,
        };
    }

    #createEntry(smartNumber: string, smartNumberMapping: SmartNumberMapping): HistoryEntry {
        return { id: crypto.randomUUID(), smartNumber, mapping: structuredClone(smartNumberMapping) };
    }
}
